import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { plural } from "../program-access/access-model";
import { programPath } from "../program-tree/program-tree";

import type { Database } from "../../__definitions__/database-definitions";
import type { ProgramNode, ProgramTree } from "../program-tree/program-tree";

export type ProgramOverview = Database["public"]["Views"]["program_overview"]["Row"];

/** What a card of the landing page shows of one program, with each folder below it. */
export interface ProgramSummary {
  node: ProgramNode;
  /** Each visible program below it, depth first. */
  folders: ProgramNode[];
  communications: number;
  active: number;
  drafts: number;
  lastEditedAt: Date | null;
  /** The active Communication that sends next, and the calendar days from today to its send. */
  nextSend: { title: string; at: Date; days: number } | null;
  /** The people's dates that its active Communications send on. */
  triggers: string[];
  /** Whether the caller keeps it at the top of the page. */
  favourite: boolean;
}

export const FILTERS = ["all", "drafts", "week"] as const;
export type ProgramFilter = (typeof FILTERS)[number];

export const filterLabels: Record<ProgramFilter, string> = {
  all: "All programs",
  drafts: "Has drafts",
  week: "Sending in the next 7 days",
};

export const SORTS = ["updated", "name", "next", "communications", "drafts"] as const;
export type ProgramSort = (typeof SORTS)[number];

export const sortLabels: Record<ProgramSort, string> = {
  updated: "Recently updated",
  name: "Name (A–Z)",
  next: "Next send",
  communications: "Most communications",
  drafts: "Most drafts to finish",
};

/** A program the search finds, and the titles of its folders that match the search. */
export interface ProgramMatch {
  summary: ProgramSummary;
  folders: string[];
}

const descendants = (node: ProgramNode): ProgramNode[] =>
  node.children.flatMap((child) => [child, ...descendants(child)]);

/**
 * The top programs that the caller can open: each top-level program, or, for a program the caller
 * cannot open, the highest programs below it that they can.
 */
const openablePrograms = (nodes: ProgramNode[]): ProgramNode[] =>
  nodes.flatMap((node) => (node.accessible ? [node] : openablePrograms(node.children)));

const nextSend = (
  overview: ProgramOverview | undefined,
  today: Date,
): ProgramSummary["nextSend"] => {
  if (!overview?.next_send_at || overview.next_send_title === null) return null;
  // Timezone naive, as the Communication's timezone has it, so it is read as a local time.
  const at = parseISO(overview.next_send_at);
  return { title: overview.next_send_title, at, days: differenceInCalendarDays(at, today) };
};

/**
 * One summary for each top program that the caller can open, in the order of the tree. A program
 * with no overview row has no Communications.
 */
export const summarisePrograms = (
  tree: ProgramTree,
  overviews: ProgramOverview[],
  favourites: ReadonlySet<string>,
  today: Date,
): ProgramSummary[] => {
  const byId = new Map(overviews.map((overview) => [overview.id, overview]));

  return openablePrograms(tree.roots).map((node) => {
    const overview = byId.get(node.id);
    return {
      node,
      folders: descendants(node),
      communications: overview?.communications ?? 0,
      active: overview?.active ?? 0,
      drafts: overview?.drafts ?? 0,
      lastEditedAt: overview?.last_edited_at ? parseISO(overview.last_edited_at) : null,
      nextSend: nextSend(overview, today),
      triggers: overview?.triggers ?? [],
      favourite: favourites.has(node.id),
    };
  });
};

export const sendsThisWeek = (summary: ProgramSummary): boolean =>
  summary.nextSend !== null && summary.nextSend.days <= 7;

const filters: Record<ProgramFilter, (summary: ProgramSummary) => boolean> = {
  all: () => true,
  drafts: ({ drafts }) => drafts > 0,
  week: sendsThisWeek,
};

export const passesFilter = (summary: ProgramSummary, filter: ProgramFilter): boolean =>
  filters[filter](summary);

/**
 * The programs whose title has the query, or that have a folder whose title has it, ignoring case.
 * An empty query finds each program, with no folders.
 */
export const searchPrograms = (summaries: ProgramSummary[], query: string): ProgramMatch[] => {
  const search = query.trim().toLowerCase();
  if (!search) return summaries.map((summary) => ({ summary, folders: [] }));

  return summaries.flatMap((summary) => {
    const folders = summary.folders
      .map(({ title }) => title)
      .filter((title) => title.toLowerCase().includes(search));
    const own = summary.node.title.toLowerCase().includes(search);
    return own || folders.length ? [{ summary, folders }] : [];
  });
};

const byTitle = (a: ProgramSummary, b: ProgramSummary): number =>
  a.node.title.localeCompare(b.node.title, undefined, { sensitivity: "base" });

/** Programs with no edit or no next send go last. Programs that are the same go by title. */
const sorters: Record<ProgramSort, (a: ProgramSummary, b: ProgramSummary) => number> = {
  updated: (a, b) =>
    (b.lastEditedAt?.getTime() ?? -Infinity) - (a.lastEditedAt?.getTime() ?? -Infinity) ||
    byTitle(a, b),
  name: byTitle,
  next: (a, b) =>
    (a.nextSend?.at.getTime() ?? Infinity) - (b.nextSend?.at.getTime() ?? Infinity) ||
    byTitle(a, b),
  communications: (a, b) => b.communications - a.communications || byTitle(a, b),
  drafts: (a, b) => b.drafts - a.drafts || byTitle(a, b),
};

export const sortMatches = (matches: ProgramMatch[], sort: ProgramSort): ProgramMatch[] =>
  matches.toSorted((a, b) => sorters[sort](a.summary, b.summary));

/** "Today", "Yesterday" or "3 days ago", for the last edit, or null with no edit. */
export const updatedShortText = (summary: ProgramSummary, today: Date): string | null => {
  if (!summary.lastEditedAt) return null;
  const days = Math.max(0, differenceInCalendarDays(today, summary.lastEditedAt));
  return days === 0 ? "Today" : days === 1 ? "Yesterday" : `${days} days ago`;
};

/** "Updated today", "Updated yesterday" or "Updated 3 days ago", or null with no edit. */
export const updatedText = (summary: ProgramSummary, today: Date): string | null => {
  const short = updatedShortText(summary, today);
  return short && `Updated ${short.charAt(0).toLowerCase()}${short.slice(1)}`;
};

/** "Mon 2 Nov", the date of a next send. */
export const sendDateText = (at: Date): string => format(at, "EEE d MMM");

/** "A", "A and B", or "A, B and C". */
export const listText = (items: string[]): string =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items.at(-1)}` : (items[0] ?? "");

/**
 * What a program sends next: the next send, or the people's dates it sends on, or why nothing is to
 * come. head and body are the card's line, and short and detail the table's.
 */
export const describeNext = (
  summary: ProgramSummary,
): {
  kind: "send" | "always" | "nothing" | "empty";
  head: string;
  body: string;
  short: string;
  detail: string;
} => {
  if (summary.nextSend) {
    const date = sendDateText(summary.nextSend.at);
    return {
      kind: "send",
      head: `Next send ${date}:`,
      body: summary.nextSend.title,
      short: date,
      detail: summary.nextSend.title,
    };
  }
  if (summary.triggers.length) {
    const triggers = listText(summary.triggers);
    return {
      kind: "always",
      head: "Always on:",
      body: `sends are triggered by ${triggers}.`,
      short: "Always on",
      detail: `Triggered by ${triggers}`,
    };
  }
  if (summary.communications) {
    return {
      kind: "nothing",
      head: "Nothing scheduled.",
      body: "No active communication has a send to come.",
      short: "—",
      detail: "Nothing scheduled",
    };
  }
  return {
    kind: "empty",
    head: "Nothing yet.",
    body: "Add the first communication to get started.",
    short: "—",
    detail: "No communications yet",
  };
};

/** The first two folders one level down, and how many more there are, or "No folders". */
export const folderText = (node: ProgramNode): string => {
  const titles = node.children.map(({ title }) => title);
  if (!titles.length) return "No folders";
  const shown = titles.slice(0, 2).join(", ");
  return titles.length > 2 ? `${shown} and ${titles.length - 2} more` : shown;
};

/** "Folder: A, B +3", the folders that match a search. */
export const matchText = (folders: string[]): string =>
  `Folder: ${folders.slice(0, 2).join(", ")}${folders.length > 2 ? ` +${folders.length - 2}` : ""}`;

/** "6 programs" with no search or filter, else "Showing 2 of 6 programs matching “mentor”". */
export const resultText = (
  shown: number,
  total: number,
  query: string,
  filter: ProgramFilter,
): string => {
  const search = query.trim();
  if (!search && filter === "all") return plural(total, "program");
  return `Showing ${shown} of ${plural(total, "program")}${search ? ` matching “${search}”` : ""}`;
};

/**
 * A favourite in the Favourites section: a program with a card, or a folder, with the titles of
 * the programs above it.
 */
export type Favourite =
  | { kind: "program"; node: ProgramNode; summary: ProgramSummary }
  | { kind: "folder"; node: ProgramNode; path: string[] };

/**
 * The caller's favourites that they can open, in the order they were starred. A program with a card
 * on the page is a program, and any other one a folder. A folder under a hidden program is left out,
 * as the tree hides it.
 */
export const favouriteItems = (
  tree: ProgramTree,
  summaries: ProgramSummary[],
  favourites: readonly string[],
): Favourite[] => {
  const byId = new Map<string, ProgramSummary>(
    summaries.map((summary) => [summary.node.id, summary]),
  );

  return favourites.flatMap((id): Favourite[] => {
    const summary = byId.get(id);
    if (summary) return [{ kind: "program", node: summary.node, summary }];
    const node = tree.byId.get(id);
    const nodes = programPath(tree, id);
    if (!node?.accessible || nodes.some(({ visible }) => !visible)) return [];
    return [{ kind: "folder", node, path: nodes.slice(0, -1).map(({ title }) => title) }];
  });
};

/**
 * "Program · 22 communications · next send Mon 2 Nov", or "· always on" for people's dates. For a
 * folder, "Folder in Career Development › Learning".
 */
export const favouriteText = (favourite: Favourite): string => {
  if (favourite.kind === "folder") return `Folder in ${favourite.path.join(" › ")}`;
  const { summary } = favourite;
  const next = summary.nextSend
    ? ` · next send ${sendDateText(summary.nextSend.at)}`
    : summary.triggers.length
      ? " · always on"
      : "";
  return `Program · ${plural(summary.communications, "communication")}${next}`;
};
